import React, { useEffect, useState, Suspense, lazy, createElement } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout/Layout';
import { ErrorBoundary } from './components/UI/ErrorBoundary';
import { PageLoader } from './components/UI/PageLoader';
import { Dialog } from './components/UI/Dialog';
import { HostSalonDialog } from './components/UI/HostSalonDialog';
import { PaymentDialog } from './components/UI/PaymentDialog';
import { CorporateTrainingDialog } from './components/UI/CorporateTrainingDialog';
import './index.css';
// Route prefetch helpers — call these on hover/intent to preload chunks
export const prefetchRoutes = {
  class: () => import('./pages/AIClass'),
  lounge: () => import('./pages/LoungeOnboard'),
  training: () => import('./pages/Training'),
  events: () => import('./pages/Events')
};
// Lazy-loaded pages — only loaded when the route is visited
const Home = lazy(() => import('./pages/Home').then(m => ({
  default: m.Home
})));
const Gallery = lazy(() => import('./pages/Gallery').then(m => ({
  default: m.Gallery
})));
const About = lazy(() => import('./pages/About').then(m => ({
  default: m.About
})));
const Blog = lazy(() => import('./pages/Blog').then(m => ({
  default: m.Blog
})));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({
  default: m.BlogPost
})));
const Contact = lazy(() => import('./pages/Contact').then(m => ({
  default: m.Contact
})));
const Toolkits = lazy(() => import('./pages/Toolkits').then(m => ({
  default: m.Toolkits
})));
const NetworkingGuide = lazy(() => import('./pages/NetworkingGuide').then(m => ({
  default: m.NetworkingGuide
})));
const FAQ = lazy(() => import('./pages/FAQ').then(m => ({
  default: m.FAQ
})));
const Partners = lazy(() => import('./pages/Partners').then(m => ({
  default: m.Partners
})));
const Events = lazy(() => import('./pages/Events').then(m => ({
  default: m.Events
})));
const SuccessStories = lazy(() => import('./pages/SuccessStories').then(m => ({
  default: m.SuccessStories
})));
const PartnerWithUs = lazy(() => import('./pages/PartnerWithUs').then(m => ({
  default: m.PartnerWithUs
})));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({
  default: m.PrivacyPolicy
})));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({
  default: m.TermsOfService
})));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy').then(m => ({
  default: m.CookiePolicy
})));
const Portfolio = lazy(() => import('./pages/Portfolio').then(m => ({
  default: m.Portfolio
})));
const Membership = lazy(() => import('./pages/Membership').then(m => ({
  default: m.Membership
})));
const JoinTeam = lazy(() => import('./pages/JoinTeam').then(m => ({
  default: m.JoinTeam
})));
const JoinNow = lazy(() => import('./pages/JoinNow').then(m => ({
  default: m.JoinNow
})));
const InnerCircle = lazy(() => import('./pages/InnerCircle').then(m => ({
  default: m.InnerCircle
})));
const LifetimePartner = lazy(() => import('./pages/LifetimePartner').then(m => ({
  default: m.LifetimePartner
})));
const WaitingRoom = lazy(() => import('./pages/WaitingRoom').then(m => ({
  default: m.WaitingRoom
})));
const WarriEvent2025 = lazy(() => import('./pages/WarriEvent2025').then(m => ({
  default: m.WarriEvent2025
})));
const HowToEngage = lazy(() => import('./pages/HowToEngage').then(m => ({
  default: m.HowToEngage
})));
const HostSalon = lazy(() => import('./pages/HostSalon').then(m => ({
  default: m.HostSalon
})));
const Spotlight = lazy(() => import('./pages/Spotlight').then(m => ({
  default: m.Spotlight
})));
const BibiEnejeSpotlight = lazy(() => import('./pages/BibiEnejeSpotlight').then(m => ({
  default: m.BibiEnejeSpotlight
})));
const AugustineSpotlight = lazy(() => import('./pages/AugustineSpotlight').then(m => ({
  default: m.AugustineSpotlight
})));
const DrOsagieSpotlight = lazy(() => import('./pages/DrOsagieSpotlight').then(m => ({
  default: m.DrOsagieSpotlight
})));
const SalonSpotlight = lazy(() => import('./pages/SalonSpotlight').then(m => ({
  default: m.SalonSpotlight
})));
const Team = lazy(() => import('./pages/Team').then(m => ({
  default: m.Team
})));
const TeamMemberDetail = lazy(() => import('./pages/TeamMemberDetail').then(m => ({
  default: m.TeamMemberDetail
})));
const AIPowerBundle = lazy(() => import('./pages/AIPowerBundle').then(m => ({
  default: m.AIPowerBundle
})));
const AIMasterclass = lazy(() => import('./pages/AIMasterclass').then(m => ({
  default: m.AIMasterclass
})));
const AIFormTest = lazy(() => import('./pages/AIFormTest').then(m => ({
  default: m.AIFormTest
})));
const AIKids = lazy(() => import('./pages/AIKids').then(m => ({
  default: m.AIKids
})));
const KidsAIProgram = lazy(() => import('./pages/KidsAIProgram').then(m => ({
  default: m.KidsAIProgram
})));
const Training = lazy(() => import('./pages/Training').then(m => ({
  default: m.Training
})));
const LoungeOnboard = lazy(() => import('./pages/LoungeOnboard').then(m => ({
  default: m.LoungeOnboard
})));
const AIClass = lazy(() => import('./pages/AIClass').then(m => ({
  default: m.AIClass
})));
const GreenEvent = lazy(() => import('./pages/GreenEvent').then(m => ({
  default: m.GreenEvent
})));
const GreenSpeakerDetail = lazy(() => import('./pages/GreenSpeakerDetail').then(m => ({
  default: m.GreenSpeakerDetail
})));
// ScrollToTop component that scrolls to top on route change
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
export function App() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isHostFormOpen, setIsHostFormOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isCorporateTrainingOpen, setIsCorporateTrainingOpen] = useState(false);
  // Preload Paystack script so it's cached before user reaches /class or payment pages
  useEffect(() => {
    // Only preload if not already present
    if (!document.querySelector('link[href*="paystack"]')) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = 'https://js.paystack.co/v1/inline.js';
      link.as = 'script';
      document.head.appendChild(link);
    }
  }, []);
  const openRegistrationForm = () => {
    setIsFormOpen(true);
  };
  const openHostSalonForm = () => {
    setIsHostFormOpen(true);
  };
  const openPaymentForm = () => {
    setIsPaymentOpen(true);
  };
  const openCorporateTrainingForm = () => {
    setIsCorporateTrainingOpen(true);
  };
  useEffect(() => {
    window.openRegistrationForm = openRegistrationForm;
    window.openHostSalonForm = openHostSalonForm;
    window.openPaymentForm = openPaymentForm;
    window.openCorporateTrainingForm = openCorporateTrainingForm;
    return () => {
      delete window.openRegistrationForm;
      delete window.openHostSalonForm;
      delete window.openPaymentForm;
      delete window.openCorporateTrainingForm;
    };
  }, []);
  return <Router data-id="element-0">
      <ScrollToTop data-id="element-1" />
      <ErrorBoundary data-id="element-2">
        <Suspense fallback={<PageLoader data-id="element-4" />} data-id="element-3">
          <Routes data-id="element-5">
            <Route path="/lounge" element={<LoungeOnboard data-id="element-7" />} data-id="element-6" />
            <Route path="/class" element={<AIClass data-id="element-9" />} data-id="element-8" />
            <Route path="/*" element={<Layout openRegistrationForm={openRegistrationForm} openHostSalonForm={openHostSalonForm} openPaymentForm={openPaymentForm} data-id="element-11">
                  <Suspense fallback={<PageLoader data-id="element-13" />} data-id="element-12">
                    <Routes data-id="element-14">
                      <Route path="/" element={<Home openRegistrationForm={openRegistrationForm} openHostSalonForm={openHostSalonForm} openPaymentForm={openPaymentForm} data-id="element-16" />} data-id="element-15" />
                      <Route path="/green" element={<GreenEvent data-id="element-18" />} data-id="element-17" />
                      <Route path="/green/speaker/:id" element={<GreenSpeakerDetail data-id="element-20" />} data-id="element-19" />
                      <Route path="/gallery" element={<Gallery data-id="element-22" />} data-id="element-21" />
                      <Route path="/about" element={<About data-id="element-24" />} data-id="element-23" />
                      <Route path="/team" element={<Team data-id="element-26" />} data-id="element-25" />
                      <Route path="/team/:id" element={<TeamMemberDetail data-id="element-28" />} data-id="element-27" />
                      <Route path="/blog" element={<Blog data-id="element-30" />} data-id="element-29" />
                      <Route path="/blog/:slug" element={<BlogPost data-id="element-32" />} data-id="element-31" />
                      <Route path="/contact" element={<Contact data-id="element-34" />} data-id="element-33" />
                      <Route path="/toolkits" element={<Toolkits data-id="element-36" />} data-id="element-35" />
                      <Route path="/networking-guide" element={<NetworkingGuide data-id="element-38" />} data-id="element-37" />
                      <Route path="/faq" element={<FAQ openRegistrationForm={openRegistrationForm} data-id="element-40" />} data-id="element-39" />
                      <Route path="/partners" element={<Partners data-id="element-42" />} data-id="element-41" />
                      <Route path="/events" element={<Events data-id="element-44" />} data-id="element-43" />
                      <Route path="/events/warri-2025" element={<WarriEvent2025 data-id="element-46" />} data-id="element-45" />
                      <Route path="/success-stories" element={<SuccessStories data-id="element-48" />} data-id="element-47" />
                      <Route path="/portfolio" element={<Portfolio data-id="element-50" />} data-id="element-49" />
                      <Route path="/membership" element={<Membership data-id="element-52" />} data-id="element-51" />
                      <Route path="/join-team" element={<JoinTeam data-id="element-54" />} data-id="element-53" />
                      <Route path="/joinnow" element={<JoinNow data-id="element-56" />} data-id="element-55" />
                      <Route path="/inner-circle" element={<InnerCircle data-id="element-58" />} data-id="element-57" />
                      <Route path="/lifetime-partner" element={<LifetimePartner data-id="element-60" />} data-id="element-59" />
                      <Route path="/waiting-room" element={<WaitingRoom data-id="element-62" />} data-id="element-61" />
                      <Route path="/join-the-salon" element={<HowToEngage data-id="element-64" />} data-id="element-63" />
                      <Route path="/host-salon" element={<HostSalon data-id="element-66" />} data-id="element-65" />
                      <Route path="/spotlight" element={<Spotlight data-id="element-68" />} data-id="element-67" />
                      <Route path="/salon-spotlight" element={<SalonSpotlight data-id="element-70" />} data-id="element-69" />
                      <Route path="/spotlight/bibi-eneje" element={<BibiEnejeSpotlight data-id="element-72" />} data-id="element-71" />
                      <Route path="/spotlight/augustine-aigbokhaevbo" element={<AugustineSpotlight data-id="element-74" />} data-id="element-73" />
                      <Route path="/spotlight/dr-osagie-alex" element={<DrOsagieSpotlight data-id="element-76" />} data-id="element-75" />
                      <Route path="/partner-with-us" element={<PartnerWithUs openRegistrationForm={openRegistrationForm} openHostSalonForm={openHostSalonForm} data-id="element-78" />} data-id="element-77" />
                      <Route path="/privacy-policy" element={<PrivacyPolicy data-id="element-80" />} data-id="element-79" />
                      <Route path="/terms-of-service" element={<TermsOfService data-id="element-82" />} data-id="element-81" />
                      <Route path="/cookie-policy" element={<CookiePolicy data-id="element-84" />} data-id="element-83" />
                      <Route path="/ai-power-bundle" element={<AIPowerBundle data-id="element-86" />} data-id="element-85" />
                      <Route path="/ai-masterclass" element={<AIMasterclass data-id="element-88" />} data-id="element-87" />
                      <Route path="/ai-form-test" element={<AIFormTest data-id="element-90" />} data-id="element-89" />
                      <Route path="/ai-kids" element={<AIKids data-id="element-92" />} data-id="element-91" />
                      <Route path="/kids-ai-program" element={<KidsAIProgram data-id="element-94" />} data-id="element-93" />
                      <Route path="/training" element={<Training data-id="element-96" />} data-id="element-95" />
                    </Routes>
                  </Suspense>
                </Layout>} data-id="element-10" />
          </Routes>
        </Suspense>
      </ErrorBoundary>

      <Dialog isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Join the Salon" size="xl" data-id="element-97">
        <div className="w-full" data-id="element-98">
          <iframe src="https://docs.google.com/forms/d/e/1FAIpQLSdr_5jvYO4hJncEC0hjHQweFJSh8FFpzrBhsjQvCQemuZT7MA/viewform?embedded=true" width="100%" height="600" frameBorder="0" marginHeight={0} marginWidth={0} data-id="element-99">
            Loading…
          </iframe>
        </div>
      </Dialog>
      <HostSalonDialog isOpen={isHostFormOpen} onClose={() => setIsHostFormOpen(false)} data-id="element-100" />
      <PaymentDialog isOpen={isPaymentOpen} onClose={() => setIsPaymentOpen(false)} data-id="element-101" />
      <CorporateTrainingDialog isOpen={isCorporateTrainingOpen} onClose={() => setIsCorporateTrainingOpen(false)} data-id="element-102" />
    </Router>;
}